import React from 'react';
import * as R from 'ramda';
// components
import TextComponent from '../../../components/text';
import { ActionBox } from '../../../components/action-box';
import EditableBox from '../../../components/editable-box';
import OpenExpandedContainer from '../../../components/open-expanded-container';
// features
import PC from '../../permission/role-permission';
import ExpenseDocument from '../../fleet-profile/driver/components/expense-document';
import { columnSettings as fuelCardsCS } from '../../fuel-cards/settings/column-settings';
import { columnSettings as expensesCS } from '../../fleet-profile/driver/settings/expenses';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// ui
import { Box, Flex } from '../../../ui';
// feature fuel-cards
import * as C from '../../fuel-cards/constants';
// feature payroll
import FuelCardDetails from '../components/fuel-card-details';
//////////////////////////////////////////////////

export const payrollChargesTableSettings = {
  allowEditBtn: true,
  tableRowHeight: 40,
  titleRowHeight: 45,
  checkBoxCellWidth: 55,
  searchableTitles: true,
  allowSelectItems: false,
  checkBoxCellJustifyContent: 'flex-start',
};

export const getPayrollChargesDefaultReportFieldNames = (isDriver) => {
  let fieldNames = [
    GC.FIELD_CHARGE_DEDUCTION,
    GC.FIELD_CHARGE_RATE_NAME,
    GC.FIELD_CHARGE_RATE,
    GC.FIELD_CHARGE_RATE_TYPE,
    GC.FIELD_CHARGE_QUANTITY,
    GC.FIELD_CHARGE_RATE_UNIT,
    GC.FIELD_CHARGE_TOTAL,
    GC.FIELD_CHARGE_CURRENCY,
    GC.FIELD_GL_CODE,
    GC.FIELD_PAYED_FOR,
    GC.FIELD_COMMENTS,
  ];

  if (isDriver) {
    fieldNames = R.compose(
      R.insert(1, GC.FIELD_DEDUCTION_FROM_VENDOR),
      R.remove(9, 1),
    )(fieldNames);
  }

  return fieldNames;
};

export const getPayrollChargesReport = (isDriver, reportName) => {
  const fieldNames = getPayrollChargesDefaultReportFieldNames(isDriver);

  const fields = fieldNames.map((name, sequence) => ({ name, sequence }));

  const guid = G.ifElse(isDriver, `driver_${reportName}`, `vendor_${reportName}`);
  const type = G.ifElse(isDriver, GC.DRIVER_PAYROLL_UI_REPORTS, GC.VENDOR_PAYROLL_UI_REPORTS);

  return {
    guid,
    type,
    fields,
  };
};

export const sortPayrollChargesReport = (isDriver, uiReportFields, report) => {
  const fields = G.isNotNilAndNotEmpty(uiReportFields)
    ? R.compose(
      R.map((name, sequence) => ({ name, sequence })),
      R.when(() => G.isTrue(isDriver), R.reject(R.equals(GC.FIELD_PAYED_FOR))),
      R.when(() => G.isFalse(isDriver), R.reject(R.equals(GC.FIELD_DEDUCTION_FROM_VENDOR))),
    )(uiReportFields)
    : R.propOr([], 'fields', report);

  return {
    ...report,
    fields,
  };
};

const getPayedForValue = ({ payedForTruck, payedForDriver }) => {
  const { fullText } = G.getUserInfo(payedForDriver);

  return G.isNotNilAndNotEmpty(fullText) ? fullText : G.getPropFromObject(GC.FIELD_UNIT_ID, payedForTruck);
};

const getRateUnitValue = ({ rateType, rateUnit }) => {
  const getLocaleFunction = R.includes(rateType, [GC.CHARGE_RATE_TYPE_TIME, GC.CHARGE_RATE_TYPE_QUANTITY])
  ? G.getEnumLocale
  : G.getUomLocale;

  let value = getLocaleFunction(rateUnit);

  if (R.equals(value, GC.THREE_DOTS_STRING)) value = rateUnit;

  return value;
};

export const payrollChargesColumnSettings = {
  [GC.FIELD_CHARGE_DEDUCTION]: {
    width: 140,
    type: 'boolean',
    name: 'titles:deduction',
    filter: { type: 'boolean' },
  },
  [GC.FIELD_DEDUCTION_FROM_VENDOR]: {
    width: 140,
    type: 'boolean',
    filter: { type: 'boolean' },
    name: 'titles:deduction-from-vendor',
  },
  [GC.FIELD_CHARGE_RATE_NAME]: {
    width: 180,
    name: 'titles:charge-name',
  },
  [GC.FIELD_CHARGE_TOTAL]: {
    width: 100,
    name: 'titles:total',
    filter: { type: 'number' },
    customComponent: ({ data: { total, currency }, callbackData: { payrollCurrency } }) => {
      const title = `${G.getCurrencySymbol(R.or(currency, payrollCurrency))} ${G.toFixed(total)}`;

      return (
        <TextComponent
          p='4px 6px'
          title={title}
          maxWidth={85}
          fontWeight={700}
          borderRadius='3px'
          width='fit-content'
          withEllipsis={true}
          bg={G.getTheme('colors.light.blue')}
          color={G.getTheme('colors.light.mainLight')}
        >
          {title}
        </TextComponent>
      );
    },
  },
  [GC.FIELD_CHARGE_CURRENCY]: {
    width: 120,
    name: 'titles:currency',
    filter: { type: 'selectMultiple', options: GC.CURRENCY_OPTIONS_2 },
  },
  [GC.FIELD_CHARGE_QUANTITY]: {
    width: 90,
    name: 'titles:qty',
    filter: { type: 'number' },
  },
  [GC.FIELD_CHARGE_RATE]: {
    width: 100,
    name: 'titles:rate',
    filter: { type: 'number' },
  },
  [GC.FIELD_CHARGE_RATE_TYPE]: {
    width: 120,
    type: 'enumLocale',
    name: 'titles:rate-type',
  },
  [GC.FIELD_CHARGE_RATE_UNIT]: {
    width: 120,
    name: 'titles:rate-unit',
    filter: {
      type: 'string',
      getFieldUiValue: ({ rateType, rateUnit }) => {
        if (G.isNilOrEmpty(rateUnit)) return '';

        return getRateUnitValue({ rateType, rateUnit });
      },
    },
    customComponent: ({ data: { rateType, rateUnit } }) => {
      if (G.isNilOrEmpty(rateUnit)) return null;

      const title = getRateUnitValue({ rateType, rateUnit });

      return (
        <TextComponent title={title} display='block' maxWidth='90%' withEllipsis={true}>
          {title}
        </TextComponent>
      );
    },
  },
  [GC.FIELD_GL_CODE]: {
    width: 100,
    sortable: false,
    searchable: false,
    name: 'titles:gl-code',
    customComponent: ({ data: { glCode }, callbackData: { asyncConfigs } }) => {
      if (G.isNilOrEmpty(glCode)) return null;

      const glCodeDisplayedValue = R.compose(
        R.pathOr('', [GC.FIELD_DISPLAYED_VALUE]),
        R.find((item) => R.equals(glCode, G.getParentGuidOrGuidFromObject(item))),
        R.pathOr([], ['dropdowns', GC.INVOICE_GL_CODE, 'options']),
      )(asyncConfigs);

      return (
        <TextComponent display='block' maxWidth='90%' withEllipsis={true} title={glCodeDisplayedValue}>
          {glCodeDisplayedValue}
        </TextComponent>
      );
    },
  },
  [GC.FIELD_PAYED_FOR]: {
    width: 160,
    name: 'titles:payed-for',
    filter: {
      type: 'string',
      getFieldUiValue: ({ payedForTruck, payedForDriver }) => {
        if (G.isAllNilOrEmpty([payedForTruck, payedForDriver])) return '';

        return getPayedForValue({ payedForTruck, payedForDriver });
      },
    },
    customComponent: ({ data: { payedForTruck, payedForDriver } }) => {
      if (G.isAllNilOrEmpty([payedForTruck, payedForDriver])) return null;

      const payedText = getPayedForValue({ payedForTruck, payedForDriver });

      return (
        <TextComponent display='block' maxWidth='90%' title={payedText} withEllipsis={true}>
          {payedText}
        </TextComponent>
      );
    },
  },
  [GC.FIELD_COMMENTS]: {
    width: 400,
    name: 'titles:comments',
    customComponent: ({ data, callbackData: { handleUpdatePayrollChargeComments } }) => (
      <Flex>
        <EditableBox
          value={R.pathOr('', [GC.FIELD_COMMENTS], data)}
          textareaStyles={{ fontSize: 11, height: '100%' }}
          wrapperStyles={{ py: '5px', height: 39, width: '100%' }}
          onBlurCallback={(comments) => handleUpdatePayrollChargeComments(comments, data)}
          placeholder={G.getWindowLocale('titles:click-to-add-a-comment', 'Click to add a comment')}
          boxStyles={{ fontSize: 11, height: '100%', display: 'flex', overflow: 'auto', alignItems: 'center' }}
        />
      </Flex>
    ),
  },
};

const TableCell = ({ data }) => {
  if (G.isNilOrEmpty(data)) return null;

  return (
    <TextComponent
      title={data}
      maxWidth='90%'
      display='block'
      withEllipsis={true}
    >
      {data}
    </TextComponent>
  );
};

export const fuelCardsColumnSettings = R.compose(
  R.mergeLeft({
    [C.RC.FUEL_CARDS_TRANSACTION_STOP_LOCATION_NAME]: {
      width: 200,
      name: 'titles:location-name',
      customComponent: ({ data }) =>
        <TableCell data={R.path([GC.FIELD_FUEL_CARDS_TRANSACTION_STOP, GC.FIELD_LOCATION_NAME], data)} />,
      filter: {
        type: 'string',
        getFieldUiValue: R.path([GC.FIELD_FUEL_CARDS_TRANSACTION_STOP, GC.FIELD_LOCATION_NAME]),
      },
    },
    [C.RC.FUEL_CARDS_TRANSACTION_STOP_CITY]: {
      width: 200,
      name: 'titles:city',
      customComponent: ({ data }) =>
        <TableCell data={R.path([GC.FIELD_FUEL_CARDS_TRANSACTION_STOP, GC.FIELD_CITY], data)} />,
      filter: {
        type: 'string',
        getFieldUiValue: R.path([GC.FIELD_FUEL_CARDS_TRANSACTION_STOP, GC.FIELD_CITY]),
      },
    },
    [C.RC.FUEL_CARDS_TRANSACTION_STOP_STATE]: {
      width: 150,
      name: 'titles:state',
      customComponent: ({ data }) =>
        <TableCell data={R.path([GC.FIELD_FUEL_CARDS_TRANSACTION_STOP, GC.FIELD_STATE], data)} />,
      filter: {
        type: 'string',
        getFieldUiValue: R.path([GC.FIELD_FUEL_CARDS_TRANSACTION_STOP, GC.FIELD_STATE]),
      },
    },
  }),
  R.pick([
    GC.FIELD_TOTAL,
    GC.FIELD_TRIP_ID,
    GC.FIELD_CURRENCY,
    GC.FIELD_TIME_ZONE,
    GC.FIELD_FUEL_CARDS_SOURCE,
    GC.FIELD_FUEL_CARDS_CARD_ID,
    GC.FIELD_FUEL_CARDS_TRANSACTION_DATE,
    GC.FIELD_FUEL_CARDS_TRANSACTION_NUMBER,
  ]),
)(fuelCardsCS);

export const fuelCardsTableSettings = {
  tableRowHeight: 40,
  titleRowHeight: 30,
  allowSelectAll: true,
  checkBoxCellWidth: 70,
  expandableItems: true,
  expandedDetailsComponent: FuelCardDetails,
};

export const fuelCardsReport = {
  fields: [
    { sequence: 0, freezed: true, name: 'removeRestoreColumn' },
    { sequence: 1, name: GC.FIELD_FUEL_CARDS_CARD_ID },
    { sequence: 2, name: GC.FIELD_FUEL_CARDS_SOURCE },
    { sequence: 3, name: GC.FIELD_FUEL_CARDS_TRANSACTION_NUMBER },
    { sequence: 4, name: GC.FIELD_FUEL_CARDS_TRANSACTION_DATE },
    { sequence: 5, name: GC.FIELD_TRIP_ID },
    { sequence: 6, name: GC.FIELD_TOTAL },
    { sequence: 7, name: GC.FIELD_CURRENCY },
    { sequence: 8, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_LOCATION_NAME },
    { sequence: 9, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_CITY },
    { sequence: 10, name: C.RC.FUEL_CARDS_TRANSACTION_STOP_STATE },
    { sequence: 11, name: GC.FIELD_TIME_ZONE },
  ],
};

export const tollTableSettings = {
  tableRowHeight: 40,
  titleRowHeight: 45,
  allowEditBtn: true,
  allowSelectAll: true,
  checkBoxCellWidth: 40,
  searchableTitles: true,
};

export const tollReport = {
  fields: [
    { sequence: 0, freezed: true, name: 'removeRestoreColumn' },
    { sequence: 1, name: GC.FIELD_TOLL_CHARGES_SOURCE },
    { sequence: 2, name: GC.FIELD_TOLL_CHARGES_AMOUNT },
    { sequence: 3, name: GC.FIELD_CURRENCY },
    { sequence: 4, name: GC.FIELD_TOLL_CHARGES_AGENCY },
    { sequence: 5, name: GC.FIELD_TOLL_CHARGES_POSITING_DATE },
    { sequence: 6, name: GC.FIELD_TOLL_CHARGES_LICENCE_PLATE },
    { sequence: 7, name: GC.FIELD_TOLL_CHARGES_LOCATION_NAME },
    { sequence: 8, name: GC.FIELD_TOLL_CHARGES_TRANSPONDER_ID },
    { sequence: 9, name: GC.FIELD_TOLL_CHARGES_TRANSACTION_DATE },
    { sequence: 10, name: GC.FIELD_TOLL_CHARGES_TRANSACTION_TYPE },
  ],
};

export const driverPayrollReport = {
  type: GC.VENDOR_PAYROLL_UI_REPORTS,
  guid: `${GC.VENDOR_PAYROLL_UI_REPORTS}DriverPayrollsReport`,
  fields: [
    { sequence: 1, name: GC.FIELD_PAYROLL_NUMBER },
    { sequence: 2, name: 'driverFirstName' },
    { sequence: 3, name: 'driverLastName' },
    { sequence: 4, name: GC.FIELD_PAYROLL_DATE_FROM },
    { sequence: 5, name: GC.FIELD_PAYROLL_DATE_TO },
    { sequence: 6, name: GC.FIELD_DEDUCTION_CHARGES_FROM_VENDOR_TOTAL },
    { sequence: 7, name: GC.FIELD_INVOICES_TOTAL },
    { sequence: 8, name: GC.FIELD_MAIN_CHARGES_TOTAL },
    { sequence: 9, name: GC.FIELD_PAYROLL_GRAND_TOTAL},
    { sequence: 10, name: GC.FIELD_CURRENCY },
  ],
};

export const addDriverPayrollReport = {
  fields: R.prepend({}, driverPayrollReport.fields),
};

export const driverPayrollColumnSettings = {
  [GC.FIELD_PAYROLL_NUMBER]: {
    width: 150,
    name: 'titles:payroll-number',
  },
  driverFirstName: {
    width: 150,
    name: ['titles:driver', 'titles:first-name'],
  },
  driverLastName: {
    width: 150,
    name: ['titles:driver', 'titles:last-name'],
  },
  [GC.FIELD_PAYROLL_DATE_FROM]: {
    filter: { type: 'date' },
    name: 'titles:date-from',
  },
  [GC.FIELD_PAYROLL_DATE_TO]: {
    name: 'titles:date-to',
    filter: { type: 'date' },
  },
  [GC.FIELD_INVOICES_TOTAL]: {
    width: 160,
    filter: { type: 'number' },
    name: 'titles:invoices-total',
  },
  [GC.FIELD_PAYROLL_GRAND_TOTAL]: {
    width: 160,
    filter: { type: 'number' },
    name: 'titles:gross-total',
  },
  [GC.FIELD_CURRENCY]: {
    width: 160,
    name: 'titles:currency',
    filter: { type: 'selectMultiple', options: GC.CURRENCY_OPTIONS_2 },
  },
  [GC.FIELD_MAIN_CHARGES_TOTAL]: {
    width: 180,
    filter: { type: 'number' },
    name: 'titles:main-charges-total',
  },
  [GC.FIELD_DEDUCTION_CHARGES_FROM_VENDOR_TOTAL]: {
    width: 180,
    filter: { type: 'number' },
    name: 'titles:additional-charges-total',
  },
};

export const advancePaymentColumnSettings = {
  [GC.FIELD_ADVANCE_PAYMENT_CHECK_AMOUNT]: {
    width: 150,
    filter: { type: 'number' },
    name: 'titles:check-amount',
  },
  [GC.FIELD_ADVANCE_PAYMENT_CHECK_FEE]: {
    width: 150,
    name: 'titles:check-fee',
    filter: { type: 'number' },
  },
  [GC.FIELD_ADVANCE_PAYMENT_TRIP_NUMBER]: {
    width: 150,
    name: 'titles:trip-number',
  },
  [GC.FIELD_ADVANCE_PAYMENT_CHECK_NUMBER]: {
    width: 200,
    name: 'titles:check-number',
  },
  [GC.FIELD_ADVANCE_PAYMENT_CHECK_BALANCE]: {
    width: 150,
    filter: { type: 'number' },
    name: 'titles:check-balance',
  },
  [GC.FIELD_ADVANCE_PAYMENT_TAG]: {
    width: 100,
    name: 'titles:tag',
  },
  [GC.FIELD_ADVANCE_PAYMENT_STATUS]: {
    width: 150,
    name: 'titles:status',
  },
  [GC.FIELD_ADVANCE_PAYMENT_CHANGE_STATUS_REASON]: {
    width: 300,
    name: 'titles:status-reason',
  },
  [GC.FIELD_ADVANCE_PAYMENT_INTEGRATION_TYPE]: {
    width: 150,
    name: 'titles:integration-type',
  },
  [GC.FIELD_ADVANCE_PAYMENT_LAST_USE_CITY]: {
    width: 200,
    name: 'titles:last-use-city',
  },
  [GC.FIELD_ADVANCE_PAYMENT_LAST_USE_LOCATION_CODE]: {
    width: 200,
    name: 'titles:last-use-location-code',
  },
  [GC.FIELD_ADVANCE_PAYMENT_LAST_USE_DATE]: {
    type: 'date',
    filter: { type: 'date' },
    name: 'titles:last-use-date',
  },
  [GC.FIELD_CREATED_DATE]: {
    type: 'date',
    filter: { type: 'date' },
    name: 'titles:created-on',
  },
  [GC.FIELD_CREATED_BY]: {
    width: 150,
    name: 'titles:created-by',
  },
  [GC.FIELD_COMMENTS]: {
    width: 400,
    name: 'titles:comments',
  },
};

const removeRestoreField = { sequence: 0, freezed: true, name: 'removeRestoreColumn' };

export const advancePaymentReport = {
  type: GC.DRIVER_PAYROLL_UI_REPORTS,
  guid: `${GC.DRIVER_PAYROLL_UI_REPORTS}AdvancePaymentsReport`,
  fields: [
    removeRestoreField,
    { sequence: 1, name: GC.FIELD_ADVANCE_PAYMENT_CHECK_AMOUNT },
    { sequence: 2, name: GC.FIELD_ADVANCE_PAYMENT_CHECK_FEE },
    { sequence: 3, name: GC.FIELD_ADVANCE_PAYMENT_TRIP_NUMBER },
    { sequence: 4, name: GC.FIELD_ADVANCE_PAYMENT_CHECK_NUMBER },
    { sequence: 5, name: GC.FIELD_ADVANCE_PAYMENT_CHECK_BALANCE },
    { sequence: 6, name: GC.FIELD_ADVANCE_PAYMENT_TAG },
    { sequence: 7, name: GC.FIELD_ADVANCE_PAYMENT_STATUS },
    { sequence: 8, name: GC.FIELD_ADVANCE_PAYMENT_CHANGE_STATUS_REASON },
    { sequence: 9, name: GC.FIELD_ADVANCE_PAYMENT_INTEGRATION_TYPE },
    { sequence: 10, name: GC.FIELD_ADVANCE_PAYMENT_LAST_USE_CITY },
    { sequence: 11, name: GC.FIELD_ADVANCE_PAYMENT_LAST_USE_LOCATION_CODE },
    { sequence: 12, name: GC.FIELD_ADVANCE_PAYMENT_LAST_USE_DATE },
    { sequence: 13, name: GC.FIELD_CREATED_DATE },
    { sequence: 14, name: GC.FIELD_CREATED_BY },
    { sequence: 15, name: GC.FIELD_COMMENTS },
  ],
};

export const getSortedReport = (fieldNames, report, noActions) => ({
  ...report,
  fields: R.compose(
    R.when(
      () => R.not(noActions),
      R.prepend(removeRestoreField),
    ),
    G.mapIndexed((name, sequence) => ({ name, sequence: R.inc(sequence) })),
  )(fieldNames),
});

export const getDefaultReportFields = (report) => R.compose(
  R.reject(R.equals('removeRestoreColumn')),
  R.pluck(GC.FIELD_NAME),
  R.prop('fields'),
)(report);

export const dailyHoursTableSettings = {
  tableRowHeight: 40,
  allowEditBtn: true,
  titleRowHeight: 45,
  checkBoxCellWidth: 0,
  searchableTitles: true,
};

export const dailyHoursColumnSettings = {
  [GC.FIELD_LOG_DATE]: {
    name: 'titles:date',
    filter: { type: 'date' },
  },
  [GC.FIELD_ON_DUTY_MINUTES]: {
    width: 150,
    filter: {
      type: 'string',
      getFieldUiValue: (data) => {
        const { shortString } = G.convertMinutesToHoursAndMinutes(G.getPropFromObject(GC.FIELD_ON_DUTY_MINUTES, data));

        return shortString;
      },
    },
    name: 'titles:on-duty-hours',
    customComponent: ({ data }) => {
      const { shortString } = G.convertMinutesToHoursAndMinutes(G.getPropFromObject(GC.FIELD_ON_DUTY_MINUTES, data));

      return (
        <Box>{shortString}</Box>
      );
    },
  },
  [GC.FIELD_DRIVING_MINUTES]: {
    width: 150,
    name: 'titles:driving-hours',
    filter: {
      type: 'string',
      getFieldUiValue: (data) => {
        const { shortString } = G.convertMinutesToHoursAndMinutes(G.getPropFromObject(GC.FIELD_DRIVING_MINUTES, data));

        return shortString;
      },
    },
    customComponent: ({ data }) => {
      const { shortString } = G.convertMinutesToHoursAndMinutes(G.getPropFromObject(GC.FIELD_DRIVING_MINUTES, data));

      return (
        <Box>{shortString}</Box>
      );
    },
  },
  [GC.FIELD_TOTAL_DISTANCE]: {
    width: 150,
    filter: { type: 'number' },
    name: 'titles:total-distance',
  },
  [GC.FIELD_TOTAL_DISTANCE_UOM]: {
    width: 180,
    name: 'titles:total-distance-uom',
  },
  [GC.FIELD_CREATED_BY]: {
    width: 150,
    name: 'titles:created-by',
  },
};

export const dailyHoursReport = {
  type: GC.DRIVER_PAYROLL_UI_REPORTS,
  guid: `${GC.DRIVER_PAYROLL_UI_REPORTS}DailyHoursReport`,
  fields: [
    { sequence: 0, name: GC.FIELD_LOG_DATE },
    { sequence: 1, name: GC.FIELD_ON_DUTY_MINUTES },
    { sequence: 2, name: GC.FIELD_DRIVING_MINUTES },
    { sequence: 3, name: GC.FIELD_TOTAL_DISTANCE },
    { sequence: 4, name: GC.FIELD_TOTAL_DISTANCE_UOM },
    { sequence: 5, name: GC.FIELD_CREATED_BY },
  ],
};

export const payrollInvoicesTableSettings = {
  maxHeight: 400,
  tableRowHeight: 40,
  titleRowHeight: 45,
  allowEditBtn: true,
  checkBoxCellWidth: 80,
  searchableTitles: true,
  allowSelectItems: false,
  checkBoxCellJustifyContent: 'center',
};

const telPrimaryReferenceValue = {
  width: 160,
  name: 'titles:trip',
  customComponent: (props) => {
    const { data, callbackData } = props;

    const { telGuid, telPrimaryReferenceValue } = data;

    const { handleOpenTrip } = callbackData;

    return (
      <ActionBox
        text={telPrimaryReferenceValue}
        action={() => handleOpenTrip(telGuid)}
      />
    );
  },
};

const truckUnitId = {
  width: 160,
  name: 'titles:truck',
  customComponent: ({ data }) => {
    const { truckGuid, truckUnitId } = data;

    if (G.isNilOrEmpty(truckUnitId)) return null;

    return (
      <OpenExpandedContainer
        guid={truckGuid}
        text={truckUnitId}
        componentType={GC.PAGE_FLEET_TRUCK_PROFILE_V2}
        permissions={[PC.FLEET_TRUCK_READ, PC.FLEET_TRUCK_WRITE]}
      />
    );
  },
};

export const commonPayrollInvoicesColumnSettings1 = {
  trailersUnitIds: {
    width: 200,
    name: 'titles:trailers',
    filter: {
      type: 'string',
      getFieldUiValue: ({ trailersUnitIds }) => {
        if (G.isNilOrEmpty(trailersUnitIds)) return '';

        return G.createStringFromArray(trailersUnitIds, ', ');
      },
    },
  },
  firstEventEarlyDate: {
    type: 'date',
    filter: { type: 'date' },
    name: ['titles:first-event', 'titles:early-date'],
  },
  firstEventCity: {
    width: 150,
    name: ['titles:first-event'],
    filter: {
      type: 'string',
      getFieldUiValue: (data) => {
        const { firstEventCity, firstEventState } = data;

        return `${firstEventCity}, ${firstEventState}`;
      },
    },
    customComponent: ({ data }) => {
      const { firstEventCity, firstEventState } = data;

      const text = `${firstEventCity}, ${firstEventState}`;

      return (
        <Box>{text}</Box>
      );
    },
  },
  lastEventEarlyDate: {
    type: 'date',
    filter: { type: 'date' },
    name: ['titles:last-event', 'titles:early-date'],
  },
  lastEventCity: {
    width: 150,
    name: ['titles:last-event'],
    filter: {
      type: 'string',
      getFieldUiValue: (data) => {
        const { lastEventCity, lastEventState } = data;

        return `${lastEventCity}, ${lastEventState}`;
      },
    },
    customComponent: ({ data }) => {
      const { lastEventCity, lastEventState } = data;

      const text = `${lastEventCity}, ${lastEventState}`;

      return (
        <Box>{text}</Box>
      );
    },
  },
  invoiceNumber: {
    width: 160,
    name: 'titles:invoice-number',
  },
  invoiceDate: {
    filter: { type: 'date' },
    name: 'titles:invoice-date',
  },
  status: {
    width: 160,
    name: 'titles:invoice-status',
  },
  fuelChargesTotal: {
    width: 100,
    name: 'titles:fuel',
    filter: { type: 'number' },
  },
  mainChargesTotal: {
    width: 160,
    filter: { type: 'number' },
    name: 'titles:main-charges',
  },
  additionalChargesTotal: {
    width: 160,
    filter: { type: 'number' },
    name: 'titles:other-charges',
  },
  currency: {
    width: 160,
    name: 'titles:currency',
    filter: { type: 'selectMultiple', options: GC.CURRENCY_OPTIONS_2 },
  },
  total: {
    width: 100,
    name: 'titles:total',
    filter: { type: 'number' },
  },
};

const commonPayrollInvoicesColumnSettings2 = {
  customerTotal: {
    width: 160,
    filter: { type: 'number' },
    name: 'titles:customer-total',
  },
  transportationMode: {
    width: 120,
    name: 'titles:mode',
  },
  glCode: {
    width: 120,
    name: 'titles:gl',
  },
  paymentDueDate: {
    name: 'titles:due-date',
    filter: { type: 'date' },
  },
  totalTripDistance: {
    width: 160,
    name: 'titles:distance',
    filter: { type: 'number' },
  },
  totalTripDistanceUom: {
    width: 160,
    name: 'titles:distance-uom',
  },
  totalTripWeight: {
    width: 120,
    name: 'titles:weight',
    filter: { type: 'number' },
  },
  totalTripWeightUom: {
    width: 160,
    name: 'titles:weight-uom',
  },
};

export const payrollInvoicesColumnSettings = {
  telPrimaryReferenceValue,
  additionalReferences: {
    width: 200,
    name: 'titles:additional-references',
  },
  truckUnitId,
  ...commonPayrollInvoicesColumnSettings1,
  ...commonPayrollInvoicesColumnSettings2,
};

export const vendorPayrollInvoicesColumnSettings = {
  telPrimaryReferenceValue,
  additionalReferences: {
    width: 200,
    name: 'titles:additional-references',
  },
  truckUnitId,
  primaryDriver: {
    width: 200,
    name: 'titles:primary-driver',
    filter: {
      type: 'string',
      getFieldUiValue: (data) => {
        const {
          primaryDriverGuid,
          primaryDriverLoginId,
          primaryDriverLastName,
          primaryDriverFirstName,
        } = data;

        if (G.isNilOrEmpty(primaryDriverGuid)) return '';

        return `${R.or(primaryDriverFirstName, '')} ${R.or(primaryDriverLastName, '')} (${primaryDriverLoginId})`;
      },
    },
    customComponent: ({ data }) => {
      const {
        primaryDriverGuid,
        primaryDriverLoginId,
        primaryDriverLastName,
        primaryDriverFirstName,
      } = data;

      if (G.isNilOrEmpty(primaryDriverGuid)) return null;

      const text = `${R.or(primaryDriverFirstName, '')} ${R.or(primaryDriverLastName, '')} (${primaryDriverLoginId})`;

      return (
        <OpenExpandedContainer
          text={text}
          guid={primaryDriverGuid}
          componentType={GC.PAGE_FLEET_DRIVER_PROFILE_V2}
          permissions={[PC.FLEET_DRIVER_READ, PC.FLEET_DRIVER_WRITE]}
        />
      );
    },
  },
  secondaryDriver: {
    width: 200,
    name: 'titles:secondary-driver',
    filter: {
      type: 'string',
      getFieldUiValue: (data) => {
        const {
          secondaryDriverGuid,
          secondaryDriverLoginId,
          secondaryDriverLastName,
          secondaryDriverFirstName,
        } = data;

        if (G.isNilOrEmpty(secondaryDriverGuid)) return '';

        return `${R.or(secondaryDriverFirstName, '')} ${
          R.or(secondaryDriverLastName, '')} (${secondaryDriverLoginId})`;
      },
    },
    customComponent: ({ data }) => {
      const {
        secondaryDriverGuid,
        secondaryDriverLoginId,
        secondaryDriverLastName,
        secondaryDriverFirstName,
      } = data;

      if (G.isNilOrEmpty(secondaryDriverGuid)) return null;

      const text = `${R.or(secondaryDriverFirstName, '')} ${
        R.or(secondaryDriverLastName, '')} (${secondaryDriverLoginId})`;

      return (
        <OpenExpandedContainer
          text={text}
          guid={secondaryDriverGuid}
          componentType={GC.PAGE_FLEET_DRIVER_PROFILE_V2}
          permissions={[PC.FLEET_DRIVER_READ, PC.FLEET_DRIVER_WRITE]}
        />
      );
    },
  },
  ...commonPayrollInvoicesColumnSettings1,
  driversTotalInvoicePay: {
    width: 160,
    filter: { type: 'number' },
    name: 'titles:driver-invoices',
  },
  ...commonPayrollInvoicesColumnSettings2,
};

export const chargesFilterProps = [
  {
    type: 'number',
    value: GC.FIELD_CHARGE_TOTAL,
    name: G.getWindowLocale('titles:total', 'Total'),
  },
  {
    type: 'string',
    value: GC.FIELD_CURRENCY,
    name: G.getWindowLocale('titles:currency', 'Currency'),
  },
  {
    type: 'string',
    value: GC.FIELD_TYPE,
    name: G.getWindowLocale('titles:type', 'Type'),
  },
  {
    type: 'number',
    value: GC.FIELD_CHARGE_QUANTITY,
    name: G.getWindowLocale('titles:quantity', 'Quantity'),
  },
  {
    type: 'string',
    value: GC.FIELD_DISPLAYED_NAME,
    name: G.getWindowLocale('titles:charge-name', 'Charge Name'),
  },
  {
    type: 'number',
    value: GC.FIELD_CHARGE_RATE,
    name: G.getWindowLocale('titles:rate', 'Rate'),
  },
  {
    type: 'string',
    value: GC.FIELD_CHARGE_RATE_TYPE,
    name: G.getWindowLocale('titles:rate-type', 'Rate Type'),
  },
  {
    type: 'string',
    value: GC.FIELD_CHARGE_RATE_UNIT,
    name: G.getWindowLocale('titles:rate-unit', 'Rate Unit'),
  },
  {
    type: 'string',
    name: G.getWindowLocale('titles:gl-code', 'GL Code'),
    value: `${GC.FIELD_GL_CODE}.${GC.FIELD_DISPLAYED_VALUE}`,
  },
  {
    type: 'boolean',
    value: GC.FIELD_CHARGE_DEDUCTION,
    name: G.getWindowLocale('titles:deduction', 'Deduction'),
  },
  {
    type: 'boolean',
    value: GC.FIELD_CHARGE_NON_TAXABLE,
    name: G.getWindowLocale('titles:non-taxable', 'Non Taxable'),
  },
  {
    type: 'string',
    value: GC.GRC.PAYROLL_NUMBER,
    name: G.getWindowLocaleArr('titles:payroll-number', 'Payroll Number'),
  },
  {
    type: 'date',
    value: GC.GRC.PAYROLL_DATE_TO,
    name: `${G.getWindowLocale('titles:payroll', 'Payroll')} ${G.getWindowLocaleArr('titles:date-to', 'Date To')}`,
  },
  {
    type: 'date',
    value: GC.GRC.PAYROLL_DATE_FROM,
    name: `${G.getWindowLocale('titles:payroll', 'Payroll')} ${G.getWindowLocaleArr('titles:date-from', 'Date From')}`,
  },
];

const driverTxtLocale = () => G.getWindowLocale('titles:driver', 'Driver');

export const driverChargesFilterProps = [
  ...chargesFilterProps,
  {
    type: 'string',
    value: GC.GRC.PAYROLL_DRIVER_LOGIN_ID,
    name: `${driverTxtLocale()} ${G.getWindowLocale('titles:user-id', 'User ID')}`,
  },
  {
    type: 'string',
    value: GC.GRC.PAYROLL_DRIVER_FIRST_NAME,
    name: `${driverTxtLocale()} ${G.getWindowLocale('titles:first-name', 'First Name')}`,
  },
  {
    type: 'string',
    value: GC.GRC.PAYROLL_DRIVER_LAST_NAME,
    name: `${driverTxtLocale()} ${G.getWindowLocale('titles:last-name', 'Last Name')}`,
  },
];

const vendorTxtLocale = () => G.getWindowLocale('titles:vendor', 'Vendor');

export const vendorChargesFilterProps = [
  ...chargesFilterProps,
  {
    type: 'string',
    value: GC.GRC.PAYROLL_FLEET_VENDOR_COMPANY_NAME,
    name: `${vendorTxtLocale()}: ${G.getWindowLocale('titles:company-name', 'Company Name')}`,
  },
  {
    type: 'string',
    value: GC.GRC.PAYROLL_FLEET_VENDOR_OWNER_NAME,
    name: `${vendorTxtLocale()}: ${G.getWindowLocale('titles:owner-name', 'Owner Name')}`,
  },
  {
    type: 'string',
    value: GC.GRC.PAYROLL_FLEET_VENDOR_MC_NUMBER,
    name: `${vendorTxtLocale()}: ${G.getWindowLocale('titles:mc-number', 'MC Number')}`,
  },
  {
    type: 'string',
    value: GC.GRC.PAYROLL_FLEET_VENDOR_ACCOUNT_NUMBER,
    name: `${vendorTxtLocale()}: ${G.getWindowLocale('titles:account-number', 'Account Number')}`,
  },
  {
    type: 'string',
    value: GC.GRC.PAYED_FOR_TRUCK_UNIT_ID,
    name: `${G.getWindowLocale('titles:payed-for', 'Payed For')}: ${G.getWindowLocale('titles:truck', 'Truck')}`,
  },
  {
    type: 'string',
    value: GC.GRC.FIELD_PAYED_FOR_DRIVER_FULL_NAME,
    name: `${G.getWindowLocale('titles:payed-for', 'Payed For')}: ${G.getWindowLocale('titles:driver', 'Driver')}`,
  },
];

export const chargesColumnSettings = {
  [GC.FIELD_CHARGE_TOTAL]: {
    width: 100,
    pivotType: 'number',
    name: 'titles:total',
  },
  [GC.FIELD_CURRENCY]: {
    width: 100,
    name: 'titles:currency',
  },
  [GC.FIELD_TYPE]: {
    width: 100,
    name: 'titles:type',
  },
  [GC.FIELD_CHARGE_QUANTITY]: {
    width: 100,
    pivotType: 'number',
    name: 'titles:quantity',
  },
  [GC.FIELD_DISPLAYED_NAME]: {
    width: 100,
    name: 'titles:charge-name',
  },
  [GC.FIELD_CHARGE_RATE]: {
    width: 100,
    name: 'titles:rate',
    pivotType: 'number',
  },
  [GC.FIELD_CHARGE_RATE_TYPE]: {
    width: 100,
    name: 'titles:rate-type',
  },
  [GC.FIELD_CHARGE_RATE_UNIT]: {
    width: 100,
    name: 'titles:rate-unit',
  },
  [`${GC.FIELD_GL_CODE}.${GC.FIELD_DISPLAYED_VALUE}`]: {
    width: 100,
    name: 'titles:gl-code',
  },
  [GC.FIELD_CHARGE_DEDUCTION]: {
    width: 100,
    name: 'titles:deduction',
  },
  [GC.FIELD_CHARGE_NON_TAXABLE]: {
    width: 100,
    name: 'titles:non-taxable',
  },
  [GC.GRC.PAYROLL_NUMBER]: {
    width: 100,
    name: 'titles:payroll-number',
  },
  [GC.GRC.PAYROLL_DATE_FROM]: {
    width: 150,
    pivotType: GC.PIVOT_TYPE_YQMD,
    name: ['titles:payroll', 'titles:date-from'],
  },
  [GC.GRC.PAYROLL_DATE_TO]: {
    width: 150,
    pivotType: GC.PIVOT_TYPE_YQMD,
    name: ['titles:payroll', 'titles:date-to'],
  },
  [GC.GRC.PAYROLL_DRIVER_LOGIN_ID]: {
    width: 100,
    name: ['titles:driver', 'titles:user-id'],
  },
  [GC.GRC.PAYROLL_DRIVER_FIRST_NAME]: {
    width: 100,
    name: ['titles:driver', 'titles:first:name'],
  },
  [GC.GRC.PAYROLL_DRIVER_LAST_NAME]: {
    width: 100,
    name: ['titles:driver', 'titles:last:name'],
  },
  [GC.GRC.PAYROLL_FLEET_VENDOR_COMPANY_NAME]: {
    type: 'string',
    name: ['titles:vendor', 'titles:company-name'],
  },
  [GC.GRC.PAYROLL_FLEET_VENDOR_OWNER_NAME]: {
    type: 'string',
    name: ['titles:vendor', 'titles:owner-name'],
  },
  [GC.GRC.PAYROLL_FLEET_VENDOR_MC_NUMBER]: {
    type: 'string',
    name: ['titles:vendor', 'titles:mc-number'],
  },
  [GC.GRC.PAYROLL_FLEET_VENDOR_ACCOUNT_NUMBER]: {
    type: 'string',
    name: ['titles:vendor', 'titles:account-number'],
  },
  [GC.GRC.PAYED_FOR_TRUCK_UNIT_ID]: {
    type: 'string',
    name: ['titles:payed-for', 'titles:truck'],
  },
  [GC.GRC.FIELD_PAYED_FOR_DRIVER_FULL_NAME]: {
    type: 'string',
    name: ['titles:payed-for', 'titles:driver'],
  },
};

export const payrollStatisticTableSettings = {
  allowEditBtn: true,
  tableRowHeight: 40,
  titleRowHeight: 45,
  checkBoxCellWidth: 0,
  searchableTitles: true,
  allowSelectItems: false,
};

export const expensesColumnSettings = R.compose(
  R.assoc(GC.FIELD_FILE_NAME, {
    width: 200,
    name: 'titles:file',
    customComponent: ({ data }) => <ExpenseDocument {...data} hideDelete={true} />,
  }),
  R.omit([
    GC.FIELD_STATUS,
    GC.FIELD_FILE_NAME,
    GC.FIELD_CREATED_BY,
    GC.FIELD_CREATED_DATE,
    GC.FIELD_PAYROLL_NUMBER,
    GC.FIELD_LAST_MODIFIED_BY,
    GC.FIELD_LAST_MODIFIED_DATE,
  ]),
)(expensesCS);

export const expensesForVendorColumnSettings = R.compose(
  R.mergeLeft({
    [`${GC.FIELD_TRUCK}.${GC.FIELD_UNIT_ID}`]: {
      width: 150,
      name: ['titles:truck', 'titles:unit-id'],
    },
    [`${GC.FIELD_TRAILER}.${GC.FIELD_UNIT_ID}`]: {
      width: 150,
      name: ['titles:trailer', 'titles:unit-id'],
    },
  }),
  R.omit([GC.FIELD_TRUCK, GC.FIELD_TRAILER]),
)(expensesColumnSettings);

export const getExpensesReport = (isDriverPayroll) => {
  const type = G.ifElse(isDriverPayroll, GC.DRIVER_PAYROLL_UI_REPORTS, GC.VENDOR_PAYROLL_UI_REPORTS);

  let fields = R.compose(
    G.mapIndexed((name, i) => ({ name, sequence: R.inc(i) })),
    R.keys,
  )(G.ifElse(isDriverPayroll, expensesColumnSettings, expensesForVendorColumnSettings));

  if (isDriverPayroll) {
    fields = R.prepend(removeRestoreField, fields);
  }

  return {
    type,
    fields,
    guid: `${type}ExpensesReport`,
  };
};

export const getRemoveRestoreColumn = (listName, removeOrRestore) => ({
  width: 60,
  sortable: false,
  searchable: false,
  notResizable: true,
  customComponent: ({ data }) => {
    const removed = G.getPropFromObject('removed', data);

    const text = G.getWindowLocale(...G.ifElse(
      removed,
      ['actions:restore', 'RESTORE'],
      ['actions:remove', 'REMOVE'],
    ));

    const color = G.getTheme(G.ifElse(
      removed,
      'colors.dark.blue',
      'colors.light.mainRed',
    ));

    return (
      <Box
        color={color}
        cursor='pointer'
        onClick={() => removeOrRestore(G.getGuidFromObject(data), listName)}
      >
        {text}
      </Box>
    );
  },
});
